import { flexRender, getCoreRowModel, useReactTable, type Row } from '@tanstack/react-table';

import type { DataTableColumnDef } from 'components/tables/data-table';
import NestedTable, { type NestedColumn } from './NestedTable';

/** The managed layout's own default floor, for a column that declares none. */
const DEFAULT_MIN_PX = 80;

type Props<T extends { id: number }> = {
    columns: DataTableColumnDef<T>[];
    data: T[];
};

/**
 * {@link NestedTable} drawing rows from list-table column definitions. A table instance hands each cell
 * its real context. A column with `fixedPx` keeps that width; the others share the rest in proportion to
 * their `minPx`. The sub-table fits its row rather than scrolling, so the floors a managed list scrolls past
 * become the proportions here, and no column gets narrower than its content: a date or an amount stays inside
 * its own cell.
 */
const ColumnDefNestedTable = <T extends { id: number }>({ columns, data }: Props<T>) => {
    // eslint-disable-next-line react-hooks-js/incompatible-library -- TanStack Table returns non-memoizable functions
    const table = useReactTable<T>({ columns, data, getCoreRowModel: getCoreRowModel() });

    const headers = table.getFlatHeaders();
    const metaOf = (columnId: string) => table.getColumn(columnId)?.columnDef.meta;
    const totalFloor = headers.reduce(
        (sum, { id }) => (metaOf(id)?.fixedPx ? sum : sum + (metaOf(id)?.minPx ?? DEFAULT_MIN_PX)),
        0
    );
    const nestedColumns = headers.map<NestedColumn<Row<T>>>((header) => {
        const meta = metaOf(header.id);
        return {
            id: header.id,
            header: flexRender(header.column.columnDef.header, header.getContext()),
            // Percentages that sum to 100% beside fixed columns: the fixed table layout keeps the fixed widths
            // and scales the others down to what is left. A calc() mixing the two would read as no width at all.
            width: meta?.fixedPx ? `${meta.fixedPx}px` : `${((meta?.minPx ?? DEFAULT_MIN_PX) / totalFloor) * 100}%`,
            Cell: (row) => {
                const cell = row.getVisibleCells().find(({ column }) => column.id === header.id);
                return cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null;
            },
        };
    });

    return (
        <NestedTable<Row<T>>
            data={table.getRowModel().rows}
            columns={nestedColumns}
            getRowKey={(row) => row.original.id}
            autoLayout
        />
    );
};

export default ColumnDefNestedTable;
